import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, EMPTY } from 'rxjs';
import { WebSocketService, WebSocketMessage, WebSocketConfig } from './websocket.service';
import { isProductionLike } from '../config/environment-utils';
import * as Sentry from '@sentry/angular';

/**
 * Sentry message sink, exposed as a DI token so tests can supply a
 * `jasmine.createSpy` at the provider seam.
 *
 * `import * as Sentry from '@sentry/angular'` is compiled by the esbuild test
 * builder into a frozen ES-module-namespace object whose members are
 * non-writable getters, so `spyOn(Sentry, 'captureMessage')` throws
 * "is not declared writable or has no setter" (confirmed 2026-04-29 — see
 * sentry.service.spec.ts). Routing the call through this token avoids spying on
 * the namespace; the default factory delegates to the real SDK so production
 * behaviour is unchanged.
 */
export const SENTRY_CAPTURE_MESSAGE = new InjectionToken<
  typeof Sentry.captureMessage
>('SENTRY_CAPTURE_MESSAGE', {
  providedIn: 'root',
  factory: () => Sentry.captureMessage,
});

/**
 * Production no-op replacement for MockWebSocketService.
 *
 * angular.json fileReplacements swaps mock-websocket.service.ts with this file
 * in production and staging builds, removing ~150 lines of mock data generators
 * from the bundle.
 *
 * This class is never instantiated in production (isAnyWsMockActive() returns
 * false), but a valid export prevents import errors without pulling in any
 * mock infrastructure. The constructor logs a warning if instantiated, since
 * that indicates a provider misconfiguration.
 */

/** Module-level dedup flag — matches pattern in mock-api.interceptor.ts */
let _misconfigWarningLogged = false;

/** @internal Reset the warning flag — test-only, not for production use. */
export function _resetMisconfigWarningForTesting(): void {
  _misconfigWarningLogged = false;
}

@Injectable()
export class MockWebSocketService extends WebSocketService {
  private readonly captureMessage = inject(SENTRY_CAPTURE_MESSAGE);

  constructor() {
    super();
    if (isProductionLike() && !_misconfigWarningLogged) {
      // allow-console: provider-misconfig fail-loud path — intentional bypass of LoggerService.
      // 1. inject(LoggerService) in a no-op stub that shouldn't exist is wasteful
      // 2. LoggerService respects enableConsoleLog:false and would suppress this warning
      // eslint-disable-next-line no-console
      console.warn(
        'MockWebSocketService (prod no-op) was instantiated. ' +
        'This indicates a provider misconfiguration — isAnyWsMockActive() should return false in production.'
      );
      // 'warning' not 'fatal' — this stub fails safe (returns EMPTY/null/false),
      // unlike mock-api.interceptor which actively serves fake data.
      this.captureMessage(
        'MockWebSocketService (prod no-op) instantiated — provider misconfiguration (isAnyWsMockActive should return false)',
        'warning'
      );
      _misconfigWarningLogged = true;
    }
  }

  override connect(_endpoint: string, _config?: Partial<WebSocketConfig>): Observable<WebSocketMessage> {
    return EMPTY;
  }
  override send(_endpoint: string, _message: unknown): boolean { return false; }
  override getConnectionState(_endpoint: string): null { return null; }
  override isConnected(_endpoint: string): boolean { return false; }
  override disconnect(_endpoint: string): void { /* no-op */ }
}
